<template>
  <div>
    <!-- 商户排行表 -->
    <div class="card mb-3">
      <div class="card-header pb-0">
        <h6>商户销售排行</h6>
        <p class="text-xs text-secondary">按收入排名，点击「详情」可下钻到设备明细与按天流水</p>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div class="table-responsive p-0">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-xs text-secondary font-weight-bolder opacity-7 text-center" style="width:50px">排名</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7">商户</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7 text-end">销售额</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7 text-end">订单数</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7 text-center">设备数</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7">占比</th>
                <th class="text-xs text-secondary opacity-7"></th>
              </tr>
            </thead>
            <tbody v-if="loading">
              <tr><td colspan="7" class="text-center text-secondary text-xs py-4">加载中...</td></tr>
            </tbody>
            <tbody v-else-if="ranking.length === 0">
              <tr><td colspan="7" class="text-center text-secondary text-xs py-4">暂无数据</td></tr>
            </tbody>
            <tbody v-else>
              <tr v-for="(item, index) in ranking" :key="item.deliver_id">
                <td class="text-center">
                  <span v-if="index === 0" class="badge bg-warning text-dark">1</span>
                  <span v-else-if="index === 1" class="badge bg-secondary">2</span>
                  <span v-else-if="index === 2" class="badge bg-danger">3</span>
                  <span v-else class="text-xs font-weight-bold">{{ item.rank }}</span>
                </td>
                <td>
                  <div class="d-flex px-2 py-1">
                    <div class="d-flex flex-column justify-content-center">
                      <h6 class="mb-0 text-sm">{{ getMerchantInfo(item.deliver_id).account_holder || item.nick_name || getMerchantInfo(item.deliver_id).name || '招财猫' }}</h6>
                      <p class="text-xs text-secondary mb-0">ID: {{ item.deliver_id }}</p>
                      <p class="text-xs text-secondary mb-0" v-if="getMerchantInfo(item.deliver_id).name">
                        进件名称: {{ getMerchantInfo(item.deliver_id).name }}
                      </p>
                      <p class="text-xs text-secondary mb-0" v-if="getMerchantInfo(item.deliver_id).mobile || getMerchantInfo(item.deliver_id).phone">
                        手机号: {{ getMerchantInfo(item.deliver_id).mobile || getMerchantInfo(item.deliver_id).phone }}
                      </p>
                      <p class="text-xs text-secondary mb-0" v-if="getMerchantInfo(item.deliver_id).address">
                        地址: {{ getMerchantInfo(item.deliver_id).address }}
                      </p>
                      <p class="text-xs text-secondary mb-0" v-if="getMerchantInfo(item.deliver_id).nick_name">
                        场地: {{ getMerchantInfo(item.deliver_id).nick_name }}
                      </p>
                    </div>
                  </div>
                </td>
                <td class="text-end"><p class="text-sm font-weight-bold mb-0">¥{{ formatMoney(item.total_fee) }}</p></td>
                <td class="text-end"><p class="text-sm font-weight-bold mb-0">{{ item.order_count }}</p></td>
                <td class="text-center"><p class="text-sm font-weight-bold mb-0">{{ item.device_count }}</p></td>
                <td>
                  <div class="d-flex align-items-center">
                    <span class="me-2 text-xs font-weight-bold">{{ item.percentage.toFixed(1) }}%</span>
                    <div style="width:80px">
                      <div class="progress" style="height:6px">
                        <div class="progress-bar" :class="getBarClass(index)" :style="{ width: Math.min(item.percentage, 100) + '%' }"></div>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="align-middle">
                  <a href="javascript:;" class="text-secondary font-weight-bold text-xs" @click="drillDown(item.deliver_id)">详情</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 商户下钻视图 -->
    <div v-if="drillDownVisible" class="card mb-3">
      <div class="card-header pb-0 d-flex justify-content-between align-items-center">
        <div>
          <h6>商户 #{{ currentDeliverId }} 下钻明细</h6>
          <p class="text-xs text-secondary">设备收入汇总 + 按天流水</p>
        </div>
        <button class="btn btn-sm btn-outline-secondary" @click="closeDrillDown">
          <i class="ni ni-fat-remove"></i> 关闭
        </button>
      </div>
      <div class="card-body">
        <!-- 子 Tab -->
        <ul class="nav nav-pills nav-sm mb-3">
          <li class="nav-item">
            <button class="nav-link" :class="{ active: drillTab === 'device' }" @click="drillTab = 'device'">设备维度</button>
          </li>
          <li class="nav-item">
            <button class="nav-link" :class="{ active: drillTab === 'daily' }" @click="drillTab = 'daily'; fetchDailyReports()">按天流水</button>
          </li>
        </ul>

        <!-- 设备维度 -->
        <div v-show="drillTab === 'device'">
          <div v-if="drillDownLoading" class="text-center text-secondary text-xs py-4">加载中...</div>
          <div v-else-if="drillDownData.length === 0" class="text-center text-secondary text-xs py-4">该商户暂无设备数据</div>
          <div v-else class="table-responsive p-0">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-xs text-secondary font-weight-bolder opacity-7">设备ID</th>
                  <th class="text-xs text-secondary font-weight-bolder opacity-7 text-end">总金额</th>
                  <th class="text-xs text-secondary font-weight-bolder opacity-7 text-end">退款</th>
                  <th class="text-xs text-secondary font-weight-bolder opacity-7 text-end">净收入</th>
                  <th class="text-xs text-secondary font-weight-bolder opacity-7 text-end">出币数</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in drillDownData" :key="item.board_id">
                  <td class="text-xs font-monospace">{{ item.board_id }}</td>
                  <td class="text-xs text-end">¥{{ formatMoney(item.sum_fee) }}</td>
                  <td class="text-xs text-end text-danger">¥{{ formatMoney(item.refund_sum_fee) }}</td>
                  <td class="text-xs text-end text-success">¥{{ formatMoney(item.net_fee) }}</td>
                  <td class="text-xs text-end">{{ (item.sum_coins / 100).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 按天流水 -->
        <div v-show="drillTab === 'daily'">
          <div v-if="dailyLoading" class="text-center text-secondary text-xs py-4">加载中...</div>
          <div v-else-if="dailyData.length === 0" class="text-center text-secondary text-xs py-4">暂无按天流水数据</div>
          <div v-else class="table-responsive p-0">
            <table class="table table-sm align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-xs text-secondary">日期</th>
                  <th class="text-xs text-secondary text-end">总金额</th>
                  <th class="text-xs text-secondary text-end">退款</th>
                  <th class="text-xs text-secondary text-end">净收入</th>
                  <th class="text-xs text-secondary text-end">出币数</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in dailyData" :key="item.date">
                  <td class="text-xs">{{ formatDate(item.date) }}</td>
                  <td class="text-xs text-end">¥{{ formatMoney(item.sum_fee) }}</td>
                  <td class="text-xs text-end text-danger">¥{{ formatMoney(item.refund_sum_fee) }}</td>
                  <td class="text-xs text-end text-success">¥{{ formatMoney(item.net_fee) }}</td>
                  <td class="text-xs text-end">{{ (item.sum_coins / 100).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 商户增长趋势 -->
    <div class="card mb-3">
      <div class="card-header pb-0">
        <h6>Top 10 商户收入趋势</h6>
        <p class="text-xs text-secondary">近 {{ days }} 天 Top 10 商户每日收入趋势</p>
      </div>
      <div class="card-body p-3">
        <canvas id="merchantTrendChart" height="120"></canvas>
        <div v-if="ranking.length === 0" class="text-center text-secondary text-xs py-4">暂无数据</div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { getMerchantSalesRanking, getSumReports, getDeliverBoardDailyReports } from "@/api/dashboard";
import { getMerchantInfoMap } from "@/utils/merchantCache";

export default {
  name: "MerchantAnalysis",
  props: {
    days: { type: Number, default: 30 },
    deliverId: { type: Number, default: 0 },
    startDate: { type: Number, required: true },
    endDate: { type: Number, required: true },
  },
  data() {
    return {
      ranking: [],
      loading: false,
      drillDownVisible: false,
      drillDownLoading: false,
      drillTab: "device",
      currentDeliverId: null,
      drillDownData: [],
      dailyData: [],
      dailyLoading: false,
      merchantInfoMap: {}, // deliver_id -> merchant info (phone, address, name, etc.)
    };
  },
  mounted() {
    this.fetchMerchantInfo();
    this.fetchRanking();
  },
  methods: {
    formatMoney(fen) {
      if (!fen) return "0.00";
      return (fen / 100).toFixed(2);
    },
    formatDate(ts) {
      const d = new Date(ts * 1000);
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
    },
    getBarClass(index) {
      if (index === 0) return "bg-info";
      if (index === 1) return "bg-success";
      if (index === 2) return "bg-warning";
      return "bg-secondary";
    },
    async fetchMerchantInfo() {
      try {
        this.merchantInfoMap = await getMerchantInfoMap();
      } catch (err) {
        console.error("获取商户信息失败:", err);
      }
    },
    getMerchantInfo(deliverId) {
      return this.merchantInfoMap[deliverId] || {};
    },
    async fetchRanking() {
      this.loading = true;
      try {
        const resp = await getMerchantSalesRanking({
          start_date: this.startDate,
          end_date: this.endDate,
          limit: 20,
        });
        if (resp.code === 0 && Array.isArray(resp.desc)) {
          this.ranking = resp.desc;
          this.$nextTick(() => this.renderTrendChart());
        }
      } catch (err) {
        console.error("获取商户排行失败:", err);
      } finally {
        this.loading = false;
      }
    },
    async drillDown(deliverId) {
      this.currentDeliverId = deliverId;
      this.drillDownVisible = true;
      this.drillTab = "device";
      this.dailyData = [];
      await this.fetchDrillDownData();
    },
    async fetchDrillDownData() {
      this.drillDownLoading = true;
      try {
        const resp = await getSumReports({
          start_date: this.startDate,
          end_date: this.endDate,
          deliver_id: this.currentDeliverId,
        });
        if (resp.code === 0 && Array.isArray(resp.desc)) {
          const boardMap = {};
          resp.desc.forEach((item) => {
            const bid = item.board_id;
            if (!boardMap[bid]) {
              boardMap[bid] = { board_id: bid, sum_fee: 0, refund_sum_fee: 0, net_fee: 0, sum_coins: 0 };
            }
            boardMap[bid].sum_fee += item.sum_fee || 0;
            boardMap[bid].refund_sum_fee += item.refund_sum_fee || 0;
            boardMap[bid].net_fee += item.net_fee || 0;
            boardMap[bid].sum_coins += item.sum_coins || 0;
          });
          this.drillDownData = Object.values(boardMap).sort((a, b) => b.sum_fee - a.sum_fee);
        }
      } catch (err) {
        console.error("商户下钻失败:", err);
      } finally {
        this.drillDownLoading = false;
      }
    },
    async fetchDailyReports() {
      if (this.dailyData.length > 0) return;
      this.dailyLoading = true;
      try {
        const resp = await getDeliverBoardDailyReports({
          start_date: this.startDate,
          end_date: this.endDate,
          deliver_id: this.currentDeliverId,
        });
        if (resp.code === 0 && Array.isArray(resp.desc)) {
          // 按天聚合
          const dayMap = {};
          resp.desc.forEach((item) => {
            const dayTs = item.start_date;
            if (!dayMap[dayTs]) {
              dayMap[dayTs] = { date: dayTs, sum_fee: 0, refund_sum_fee: 0, net_fee: 0, sum_coins: 0 };
            }
            dayMap[dayTs].sum_fee += item.sum_fee || 0;
            dayMap[dayTs].refund_sum_fee += item.refund_sum_fee || 0;
            dayMap[dayTs].net_fee += item.net_fee || 0;
            dayMap[dayTs].sum_coins += item.sum_coins || 0;
          });
          this.dailyData = Object.values(dayMap).sort((a, b) => a.date - b.date);
        }
      } catch (err) {
        console.error("获取按天流水失败:", err);
      } finally {
        this.dailyLoading = false;
      }
    },
    closeDrillDown() {
      this.drillDownVisible = false;
      this.currentDeliverId = null;
      this.drillDownData = [];
      this.dailyData = [];
    },
    renderTrendChart() {
      const ctx = document.getElementById("merchantTrendChart");
      if (!ctx) return;
      const existing = Chart.getChart("merchantTrendChart");
      if (existing) existing.destroy();

      const top10 = this.ranking.slice(0, 10);
      if (top10.length === 0) return;

      const colors = ["#cb0c9f", "#3A416F", "#2dce89", "#11cdef", "#fb6340", "#5e72e4", "#ffd600", "#f5365c", "#8965e0", "#525f7f"];
      new Chart(ctx.getContext("2d"), {
        type: "bar",
        data: {
          labels: top10.map((m) => this.getMerchantInfo(m.deliver_id).account_holder || m.nick_name || this.getMerchantInfo(m.deliver_id).name || '招财猫'),
          datasets: [{
            label: "收入(分)",
            data: top10.map((m) => m.total_fee),
            backgroundColor: colors,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { grid: { borderDash: [5, 5] }, ticks: { color: "#b2b9bf", font: { size: 11 } } },
            x: { grid: { display: false }, ticks: { color: "#b2b9bf", font: { size: 10 } } },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.nav-pills .nav-link {
  color: #6c757d;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}
.nav-pills .nav-link:hover {
  color: #344767;
  background-color: rgba(52, 71, 103, 0.08);
}
.nav-pills .nav-link.active {
  color: #fff;
  background-color: #344767;
  border-color: #344767;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(52, 71, 103, 0.2);
}
</style>
